import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  DeviceDto,
  DeviceListDto,
  DeviceLifecycleInput,
  DeviceLifecyclePreviewDto,
  DeviceOperationDto,
  DeviceRegistrationRequestDto,
} from '@onig-control/contracts';
import { api } from './api';
import { Drawer, ErrorMessage, Pagination } from './components';
import { CompanyPicker } from './DeviceForm';
import type { Assignment } from './types';

const labels = {
  UNLINK: 'Aus einer App entfernen',
  ARCHIVE: 'Gerät überall stilllegen',
  RESTORE: 'Gerät wiederherstellen',
  TRANSFER: 'Kundenwechsel',
  MERGE: 'Doppeltes Gerät zusammenführen',
};
const statuses: Record<string, string> = {
  PENDING: 'Ausstehend',
  RUNNING: 'In Bearbeitung',
  PARTIAL: 'Teilweise umgesetzt',
  FAILED: 'Fehlgeschlagen',
  MANUAL_REQUIRED: 'App muss noch angebunden werden',
  SUCCEEDED: 'Bestätigt',
};

export function DeviceLifecycle({
  device,
  onChanged,
}: {
  device: DeviceDto;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<DeviceLifecycleInput['action']>(
    device.status === 'ARCHIVED' ? 'RESTORE' : 'ARCHIVE',
  );
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [instance, setInstance] = useState('');
  const [company, setCompany] = useState('');
  const [search, setSearch] = useState('');
  const [devices, setDevices] = useState<DeviceDto[]>([]);
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState<DeviceLifecyclePreviewDto>();
  const [operations, setOperations] = useState<DeviceOperationDto[]>([]);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const load = useCallback(async () => {
    const result = await api<{ items: DeviceOperationDto[] }>(
      `/api/devices/${device.id}/operations`,
    );
    setOperations(result.items);
  }, [device.id]);
  useEffect(() => {
    void load().catch(setError);
  }, [load, device.revision]);
  const pending = operations.some((operation) => operation.status !== 'SUCCEEDED');
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => {
      void load().then(onChanged).catch(setError);
    }, 5000);
    return () => clearInterval(timer);
  }, [pending, load, onChanged]);
  useEffect(() => {
    if (!open) return;
    void api<Assignment[]>(`/api/entities/DEVICE/${encodeURIComponent(device.id)}/assignments`)
      .then(setAssignments)
      .catch(setError);
  }, [open, device.id]);
  useEffect(() => {
    if (!open || action !== 'MERGE') return;
    const abort = new AbortController();
    const timer = setTimeout(() => {
      const query = new URLSearchParams({
        search,
        status: 'ACTIVE',
        ...(device.companyId ? { companyId: device.companyId } : { unassigned: 'true' }),
      });
      void api<DeviceListDto>(`/api/devices?${query}`, { signal: abort.signal })
        .then((result) => setDevices(result.items.filter((item) => item.id !== device.id)))
        .catch((e) => {
          if (!abort.signal.aborted) setError(e);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [open, action, search, device.id, device.companyId]);
  const input: DeviceLifecycleInput = {
    action,
    integrationInstanceIds: action === 'UNLINK' && instance ? [instance] : [],
    ...(action === 'TRANSFER' && company ? { targetCompanyId: company } : {}),
    ...(action === 'MERGE' && target ? { targetDeviceId: target } : {}),
  };
  const run = async (execute: boolean) => {
    setBusy(true);
    setError(undefined);
    try {
      if (!execute) {
        setPreview(
          await api<DeviceLifecyclePreviewDto>(`/api/devices/${device.id}/lifecycle/preview`, {
            method: 'POST',
            revision: device.revision,
            body: JSON.stringify(input),
          }),
        );
        setIdempotencyKey(crypto.randomUUID());
      } else if (preview) {
        await api(`/api/devices/${device.id}/lifecycle`, {
          method: 'POST',
          revision: preview.device.revision,
          idempotencyKey,
          body: JSON.stringify({ ...input, previewHash: preview.previewHash }),
        });
        setOpen(false);
        setPreview(undefined);
        await load();
        await onChanged();
      }
    } catch (e) {
      setError(e);
      if (execute) {
        setPreview(undefined);
        await onChanged().catch(setError);
      }
    } finally {
      setBusy(false);
    }
  };
  if (device.mergedIntoId)
    return (
      <p className="panel p-4">
        Dieses Gerät wurde zusammengeführt.{' '}
        <Link to={`/devices/${device.mergedIntoId}`} className="text-brand">
          Verbleibendes Gerät öffnen
        </Link>
      </p>
    );
  return (
    <section className="panel my-4 p-5 space-y-3" aria-label="Geräteverwaltung">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p>Apps verwalten, Installationen ersetzen und Geräte nachvollziehbar stilllegen.</p>
        <button
          className="button-secondary"
          disabled={busy || pending}
          onClick={() => {
            setAction(device.status === 'ARCHIVED' ? 'RESTORE' : 'ARCHIVE');
            setPreview(undefined);
            setError(undefined);
            setOpen(true);
          }}
        >
          Gerät verwalten
        </button>
      </div>
      <ErrorMessage error={error} />
      {operations.slice(0, 5).map((operation) => (
        <details
          key={operation.id}
          open={operation.status !== 'SUCCEEDED'}
          className="border-t border-line pt-3"
        >
          <summary>
            {labels[operation.action as keyof typeof labels] ?? operation.action} ·{' '}
            {statuses[operation.status] ?? operation.status}
          </summary>
          <ul className="my-2 space-y-1">
            {operation.targets.map((item) => (
              <li key={item.id}>
                {assignments.find(
                  (assignment) => assignment.integrationInstanceId === item.integrationInstanceId,
                )?.instance.name ?? item.integrationInstanceId}
                : {statuses[item.status] ?? item.status}
                {item.errorCode ? ` (${item.errorCode})` : ''}
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted">
            Bestätigt werden die Sperren in den beteiligten Apps. Eine lokale Deinstallation wird
            nicht ausgeführt. Historische Daten bleiben erhalten.
          </p>
          {['PARTIAL', 'FAILED', 'MANUAL_REQUIRED'].includes(operation.status) && (
            <button
              className="button-secondary mt-2"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void api(`/api/device-operations/${operation.id}/retry`, {
                  method: 'POST',
                  revision: device.revision,
                })
                  .then(load)
                  .catch(setError)
                  .finally(() => setBusy(false));
              }}
            >
              Offene Schritte wiederholen
            </button>
          )}
        </details>
      ))}
      {open && (
        <Drawer
          title={labels[action]}
          onClose={() => {
            if (!busy) setOpen(false);
          }}
        >
          <div className="space-y-4">
            <ErrorMessage error={error} />
            <label className="block">
              <span className="label">Aktion</span>
              <select
                className="input w-full"
                value={action}
                disabled={busy}
                onChange={(event) => {
                  setAction(event.target.value as DeviceLifecycleInput['action']);
                  setPreview(undefined);
                }}
              >
                {Object.entries(labels)
                  .filter(([key]) =>
                    device.status === 'ARCHIVED' ? key === 'RESTORE' : key !== 'RESTORE',
                  )
                  .map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
              </select>
            </label>
            {action === 'UNLINK' && (
              <label className="block">
                <span className="label">App</span>
                <select
                  className="input w-full"
                  value={instance}
                  disabled={busy}
                  onChange={(event) => {
                    setInstance(event.target.value);
                    setPreview(undefined);
                  }}
                >
                  <option value="">App auswählen</option>
                  {assignments.map((item) => (
                    <option key={item.id} value={item.integrationInstanceId}>
                      {item.instance.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {action === 'TRANSFER' && (
              <>
                <CompanyPicker
                  value={company}
                  emptyLabel="Zielkunden auswählen"
                  onChange={(value) => {
                    setCompany(value);
                    setPreview(undefined);
                  }}
                />
                <p>
                  Vor der Übergabe werden alle bisherigen App-Zugänge gesperrt. Alte Inventare,
                  Schlüssel und Aufzeichnungen bleiben beim bisherigen Kunden. Apps müssen
                  anschließend neu verbunden werden.
                </p>
              </>
            )}
            {action === 'MERGE' && (
              <>
                <label className="block">
                  <span className="label">Verbleibendes Gerät suchen</span>
                  <input
                    className="input w-full"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </label>
                <select
                  aria-label="Verbleibendes Gerät"
                  className="input w-full"
                  value={target}
                  onChange={(event) => {
                    setTarget(event.target.value);
                    setPreview(undefined);
                  }}
                >
                  <option value="">Gerät auswählen</option>
                  {devices.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} · {item.hostname}
                    </option>
                  ))}
                </select>
                <p>
                  Die Installationen dieses doppelten Eintrags werden beendet. Seine Historie bleibt
                  erhalten und verweist anschließend auf das verbleibende Gerät. Alte Zugriffsrechte
                  werden nicht übertragen.
                </p>
              </>
            )}
            {action === 'RESTORE' && (
              <p>
                Die zentrale Geräteidentität wird wieder aktiv. Bisher widerrufene Zugänge bleiben
                gesperrt. Neue Installationen werden in der jeweiligen App freigegeben.
              </p>
            )}
            {preview ? (
              <div className="rounded border border-line p-4 space-y-3">
                <strong>{preview.device.name}</strong>
                <p>
                  {preview.targetCompany
                    ? `Neuer Kunde: ${preview.targetCompany.name}`
                    : preview.targetDevice
                      ? `Verbleibendes Gerät: ${preview.targetDevice.name}`
                      : labels[action]}
                </p>
                <ul>
                  {preview.targets.map((item) => (
                    <li key={item.integrationInstanceId}>
                      {item.name}: Zugriff und Erfassung beenden
                      {item.application === 'rustdesk' ? ', laufende Verbindungen prüfen' : ''}.
                      {!item.supported && (
                        <span className="text-amber-800">
                          {' '}
                          Unterstützung fehlt – der Vorgang bleibt bis zur Umsetzung offen.
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <p>
                  Historie bleibt erhalten. Eine Deinstallation auf dem Gerät ist nicht enthalten.
                </p>
                <button className="button" disabled={busy} onClick={() => void run(true)}>
                  {busy ? 'Wird gespeichert…' : `${labels[action]} bestätigen`}
                </button>
              </div>
            ) : (
              <button
                className="button"
                disabled={
                  busy ||
                  (action === 'UNLINK' && !instance) ||
                  (action === 'TRANSFER' && !company) ||
                  (action === 'MERGE' && !target)
                }
                onClick={() => void run(false)}
              >
                Auswirkungen prüfen
              </button>
            )}
          </div>
        </Drawer>
      )}
    </section>
  );
}

export function DeviceRegistrations() {
  const [items, setItems] = useState<DeviceRegistrationRequestDto[]>([]),
    [total, setTotal] = useState(0),
    [page, setPage] = useState(1);
  const [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState<{
    request: DeviceRegistrationRequestDto;
    target?: DeviceRegistrationRequestDto['candidates'][number];
    reject?: boolean;
  }>();
  const load = useCallback(async () => {
    const result = await api<{ items: DeviceRegistrationRequestDto[]; total: number }>(
      `/api/device-registrations?page=${page}&pageSize=10`,
    );
    setItems(result.items);
    setTotal(result.total);
  }, [page]);
  useEffect(() => {
    void load().catch(setError);
  }, [load]);
  return (
    <section className="panel my-4 p-5">
      <ErrorMessage error={error} />
      <details open={total > 0}>
        <summary className="font-medium">Gerätezuordnungen prüfen ({total})</summary>
        {!items.length && <p className="mt-2 text-muted">Keine offenen Zuordnungskonflikte.</p>}
        {items.map((request) => (
          <div key={request.id} className="my-3 border-t border-line pt-3">
            <strong>{request.hostname || 'Neue Registrierung'}</strong>
            <p>Anmeldung für: {request.companyName ?? request.companyId}</p>
            <p>
              {request.reason === 'DEVICE_COMPANY_CONFLICT'
                ? 'Passende Gerätekennung bei einem anderen Kunden gefunden. Die neue Registrierung wurde angehalten.'
                : 'Passende Gerätekennung bereits vorhanden. Bestehendes Gerät prüfen.'}
            </p>
            <ul className="my-2 space-y-2">
              {request.candidates.map((candidate) => (
                <li key={candidate.id}>
                  <Link className="text-brand" to={`/devices/${candidate.id}`}>
                    {candidate.name}
                  </Link>{' '}
                  · {candidate.company?.name ?? 'Ohne Kunden'} · {candidate.status}
                  {candidate.companyId === request.companyId && candidate.status === 'ACTIVE' ? (
                    <button
                      className="button-secondary ml-2"
                      onClick={() => setDecision({ request, target: candidate })}
                    >
                      Diesem Gerät zuordnen
                    </button>
                  ) : (
                    <span className="ml-2 text-muted">
                      Kundenwechsel oder Wiederherstellung zuerst am bestehenden Gerät durchführen.
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <button className="button-secondary" onClick={() => setDecision({ request })}>
              Als eigenständiges Gerät prüfen
            </button>
          </div>
        ))}
        {total > 10 && (
          <Pagination page={page} pageSize={10} total={total} onPageChange={setPage} />
        )}
      </details>
      {decision && (
        <Drawer
          title="Gerätezuordnung bestätigen"
          onClose={() => {
            if (!busy) setDecision(undefined);
          }}
        >
          <ErrorMessage error={error} />
          <p className="mb-4">
            {decision.reject
              ? 'Diese Anmeldung bleibt gesperrt und verschwindet aus der Prüfliste. Eine spätere Registrierung benötigt eine neue Installations-ID.'
              : decision.target
                ? `${decision.request.hostname} wird dem vorhandenen Gerät ${decision.target.name} zugeordnet. Eine noch aktive Installation derselben App muss zuvor beendet sein.`
                : `${decision.request.hostname} wird ausdrücklich als anderes Gerät angelegt. Gleiche Hardwarekennungen können beispielsweise bei Klonen vorkommen. Bestehende Kundendaten und Zugänge werden nicht übernommen.`}
          </p>
          <button
            className="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setError(undefined);
              void api(`/api/device-registrations/${decision.request.id}/resolve`, {
                method: 'POST',
                revision: decision.request.revision,
                body: JSON.stringify(
                  decision.reject
                    ? { action: 'REJECT' }
                    : decision.target
                      ? {
                          action: 'LINK',
                          targetDeviceId: decision.target.id,
                          targetRevision: decision.target.revision,
                        }
                      : { action: 'CREATE_NEW' },
                ),
              })
                .then(async () => {
                  setDecision(undefined);
                  await load();
                })
                .catch(setError)
                .finally(() => setBusy(false));
            }}
          >
            Zuordnung bestätigen
          </button>
        </Drawer>
      )}
    </section>
  );
}
